// ✅ src/pages/VehicleV2/VehicleBataDetails.jsx  (UPDATED FULL FILE)
// Backend mount:
//   app.use("/api/vehicleBetaDetails", vehicleBetaDetails);
//
// APIs used:
//   GET  http://nissi-india.com/api/vehicleBetaDetails/init?branchId=&loginUserId=&branchType=
//   GET  http://nissi-india.com/api/vehicleBetaDetails/list?branchId=&branchType=&loginUserId=&vehicleType=own|hire&fromDate=&toDate=&storeId=&supplier=&vehicleNumber=&driverName=&search=&page=&limit=
//
// ✅ Updated pattern:
// - shared localStorage / sessionStorage header pattern
// - useMemo + useCallback pattern
// - safer init / list loading
// - same UI preserved

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaFilter,
  FaFileExport,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/vehicleBetaDetails";

/* ---------------- helpers ---------------- */
const toStr = (v, d = "") => {
  if (v === null || v === undefined) return d;
  return String(v).trim();
};

const clean = (v) => String(v ?? "").trim();

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const formatYYYYMMDD = (d) => (d ? d : "");

const VehicleBataDetails = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchTypeRaw =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const branchId = useMemo(() => toInt(branchIdRaw, 0), [branchIdRaw]);
  const branchType = useMemo(() => toStr(branchTypeRaw || "HO"), [branchTypeRaw]);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchIdRaw,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchIdRaw, branchType, userId]
  );

  // UI state
  const [vehicleType, setVehicleType] = useState("own"); // own | hire
  const [showFilters, setShowFilters] = useState(true);
  const [search, setSearch] = useState("");

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    storeId: "",
    supplier: "",
    vehicleNumber: "",
    driverName: "",
  });

  // dropdowns
  const [stores, setStores] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // list state
  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  // paging
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    const t = toInt(total, 0);
    const l = toInt(limit, 50);
    return Math.max(1, Math.ceil(t / l));
  }, [total, limit]);

  const [loading, setLoading] = useState(false);

  // UI classes
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- init dropdowns ---------------- */
  const loadInit = useCallback(async () => {
    try {
      if (!branchId) return;

      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId: toInt(branchId),
          loginUserId: toInt(loginUserId),
          branchType: toStr(branchType),
        },
        headers: commonHeaders,
      });

      if (data?.success) {
        setStores(Array.isArray(data.stores) ? data.stores : []);
        setSuppliers(Array.isArray(data.suppliers) ? data.suppliers : []);
      } else {
        toast.error(data?.message || "Init failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Init error");
    }
  }, [branchId, loginUserId, branchType, commonHeaders]);

  /* ---------------- list loader ---------------- */
  const loadList = useCallback(
    async (opts = {}) => {
      try {
        setLoading(true);

        const params = {
          branchId: toInt(branchId),
          branchType: toStr(branchType),
          loginUserId: toInt(loginUserId),

          vehicleType, // own | hire
          fromDate: formatYYYYMMDD(filters.fromDate),
          toDate: formatYYYYMMDD(filters.toDate),

          storeId: filters.storeId ? toInt(filters.storeId) : "",
          supplier: toStr(filters.supplier),
          vehicleNumber: toStr(filters.vehicleNumber),
          driverName: toStr(filters.driverName),

          search: toStr(search),

          page: opts.page ?? page,
          limit: opts.limit ?? limit,
        };

        const { data } = await axios.get(`${API_BASE}/list`, {
          params,
          headers: commonHeaders,
        });

        if (data?.success) {
          setRows(Array.isArray(data.list) ? data.list : []);
          setTotal(toInt(data.total, 0));
          setGrandTotal(Number(data.grandTotal || 0));
          setPage(toInt(data.page, params.page));
          setLimit(toInt(data.limit, params.limit));
        } else {
          setRows([]);
          setTotal(0);
          setGrandTotal(0);
          toast.warn(data?.message || "No records");
        }
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "List loading error"
        );
      } finally {
        setLoading(false);
      }
    },
    [
      branchId,
      branchType,
      loginUserId,
      vehicleType,
      filters,
      search,
      page,
      limit,
      commonHeaders,
    ]
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (branchId && loginUserId) {
      loadList({ page: 1 });
    }
  }, [branchId, loginUserId, loadList]);

  useEffect(() => {
    if (vehicleType === "own") {
      setFilters((p) => ({ ...p, supplier: "" }));
    }

    setPage(1);

    if (branchId && loginUserId) {
      loadList({ page: 1 });
    }
  }, [vehicleType]); // intentionally kept like your flow

  /* ---------------- handlers ---------------- */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  }, []);

  const onSearchClick = useCallback(() => {
    setPage(1);
    loadList({ page: 1 });
    toast.info("Searching...");
  }, [loadList]);

  const onViewAll = useCallback(() => {
    setSearch("");
    setFilters({
      fromDate: "",
      toDate: "",
      storeId: "",
      supplier: "",
      vehicleNumber: "",
      driverName: "",
    });
    setPage(1);
    loadList({ page: 1 });
  }, [loadList]);

  const onExport = useCallback(async () => {
    try {
      if (!rows?.length) {
        toast.info("No rows to export");
        return;
      }

      const header = [
        "S.No",
        "Date",
        "Vehicle Number",
        "Vehicle Name",
        "Driver",
        "Supplier",
        "Amount",
      ];

      const lines = rows.map((r, i) => [
        i + 1,
        r.date ?? r.entryDate ?? "",
        r.number ?? r.vehicleRegNo ?? "",
        r.name ?? r.vehicleName ?? "",
        r.driver ?? r.driverName ?? "",
        r.supplier ?? "",
        Number(r.amount ?? 0).toFixed(2),
      ]);

      const csv = [header, ...lines]
        .map((arr) =>
          arr
            .map((x) => `"${String(x ?? "").replaceAll('"', '""')}"`)
            .join(",")
        )
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `vehicle_bata_details_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      a.click();

      URL.revokeObjectURL(url);
      toast.success("Exported CSV");
    } catch (e) {
      console.error(e);
      toast.error("Export failed");
    }
  }, [rows]);

  const goPrev = useCallback(() => {
    const p = Math.max(1, page - 1);
    setPage(p);
    loadList({ page: p });
  }, [page, loadList]);

  const goNext = useCallback(() => {
    const p = Math.min(totalPages, page + 1);
    setPage(p);
    loadList({ page: p });
  }, [page, totalPages, loadList]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE BETA DETAILS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Bata Details
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>

              {loading && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters((v) => !v)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={onExport}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark ? "bg-green-700 hover:bg-green-600 text-white" : "bg-green-600 hover:bg-green-700 text-white"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter /> FILTRATION AREA
              </h3>

              {/* Vehicle type */}
              <div className="mb-6">
                <label className={labelClass}>Vehicle Type</label>
                <div className="flex space-x-6">
                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="vehicleType"
                      value="own"
                      checked={vehicleType === "own"}
                      onChange={(e) => setVehicleType(e.target.value)}
                      className="form-radio text-blue-600"
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Own Vehicle
                    </span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="vehicleType"
                      value="hire"
                      checked={vehicleType === "hire"}
                      onChange={(e) => setVehicleType(e.target.value)}
                      className="form-radio text-blue-600"
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      Hire Vehicle
                    </span>
                  </label>
                </div>
              </div>

              {/* Search box */}
              <div className="mb-6">
                <label className={labelClass}>Search</label>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={inputClass}
                  placeholder="Search vehicle no / name / driver / supplier..."
                />
              </div>

              {/* grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Store</label>
                  <select
                    name="storeId"
                    value={filters.storeId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select Store</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Supplier</label>
                  <select
                    name="supplier"
                    value={filters.supplier}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={vehicleType === "own"}
                  >
                    <option value="">
                      {vehicleType === "own" ? "Disabled for Own" : "Select Supplier"}
                    </option>
                    {suppliers.map((x, idx) => (
                      <option key={`${x.id}-${idx}`} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Number</label>
                  <input
                    name="vehicleNumber"
                    value={filters.vehicleNumber}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Type reg no..."
                  />
                </div>

                <div>
                  <label className={labelClass}>Driver Name</label>
                  <input
                    name="driverName"
                    value={filters.driverName}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Type driver..."
                  />
                </div>
              </div>

              {/* buttons */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={onViewAll}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-3">
                  <button
                    onClick={onSearchClick}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`}
                    disabled={loading}
                  >
                    <FaSearch />
                    Search
                  </button>

                  <button
                    onClick={onViewAll}
                    className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    disabled={loading}
                  >
                    View All
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaSearch className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>
                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Number</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                      <th className="px-4 py-4 text-center font-semibold">Driver</th>
                      <th className="px-4 py-4 text-center font-semibold">Supplier</th>
                      <th className="px-4 py-4 text-center font-semibold">Amount</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => {
                      const date = r.date ?? r.entryDate ?? "";
                      const number = r.number ?? r.vehicleRegNo ?? "";
                      const name = r.name ?? r.vehicleName ?? "";
                      const driver = r.driver ?? r.driverName ?? "";
                      const supplier = r.supplier ?? "";
                      const amount = Number(r.amount ?? 0);

                      return (
                        <tr
                          key={`${number}-${idx}`}
                          className={`${isDark ? "bg-gray-900" : "bg-white"} transition-colors`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {idx + 1 + (page - 1) * limit}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {date}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {number}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {name}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {driver || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {supplier || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {Number.isFinite(amount) ? amount.toFixed(2) : "0.00"}
                          </td>
                        </tr>
                      );
                    })}

                    <tr className={`font-bold ${isDark ? "bg-gray-800 text-white" : "bg-gray-100 text-gray-900"}`}>
                      <td colSpan="6" className="px-4 py-4 text-right">
                        Grand Total
                      </td>
                      <td className="px-4 py-4 text-right">
                        {Number(grandTotal || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6">
            <div className={`${isDark ? "text-gray-300" : "text-gray-700"} text-sm`}>
              Page <b>{page}</b> of <b>{totalPages}</b> • Total: <b>{total}</b>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={goPrev}
                disabled={loading || page <= 1}
                className={`px-3 py-2 rounded border ${
                  isDark
                    ? "border-gray-700 text-white bg-gray-800 disabled:opacity-50"
                    : "border-gray-300 text-gray-700 bg-white disabled:opacity-50"
                }`}
              >
                <FaChevronLeft />
              </button>

              <select
                value={limit}
                onChange={(e) => {
                  const newLimit = toInt(e.target.value, 50);
                  setLimit(newLimit);
                  setPage(1);
                  loadList({ page: 1, limit: newLimit });
                }}
                className={inputClass}
                style={{ width: 120 }}
                disabled={loading}
              >
                {[20, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              <button
                onClick={goNext}
                disabled={loading || page >= totalPages}
                className={`px-3 py-2 rounded border ${
                  isDark
                    ? "border-gray-700 text-white bg-gray-800 disabled:opacity-50"
                    : "border-gray-300 text-gray-700 bg-white disabled:opacity-50"
                }`}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleBataDetails;